import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Layout } from '@layout/Layout';
import usePlanBoardStore, { BoardRow, DayStatus } from '@/store/hr/PlanBoardStore';
import { DayStatusChip, formatRate } from '@components/hr/planReports/TaskStatusChip';
import EmployeeDayDialog from '@components/hr/planReports/EmployeeDayDialog';

/** The exception filters. These are derived from the board rows, never re-fetched,
 *  so the chips and the table can't disagree. */
type ExceptionFilter = 'all' | 'no_plan' | 'forgot_eod' | 'late' | 'open_tasks';

const KPI_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
];

const KpiCard: React.FC<{ label: string; value: React.ReactNode; sub?: string; gradient: string }> = ({
  label,
  value,
  sub,
  gradient
}) => (
  <Card sx={{ background: gradient, color: '#fff', borderRadius: 2 }}>
    <CardContent>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.9 }}
      >
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const timeOf = (v: string | null) =>
  v ? new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

const PlanBoardPage: React.FC = () => {
  const theme = useTheme();
  const board = usePlanBoardStore((s) => s.board);
  const loading = usePlanBoardStore((s) => s.loading);
  const error = usePlanBoardStore((s) => s.error);
  const fetchBoard = usePlanBoardStore((s) => s.fetchBoard);

  // Deliberately NOT `new Date().toISOString().split('T')[0]` — between midnight
  // and 05:30 IST that returns YESTERDAY. Start empty and let the server tell us
  // its business date, then pin the input to it.
  const [date, setDate] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ExceptionFilter>('all');
  const [selected, setSelected] = useState<BoardRow | null>(null);

  useEffect(() => {
    fetchBoard(date ? { date } : {});
  }, [date, fetchBoard]);

  // Adopt the server's business date once it arrives, so the picker is correct.
  useEffect(() => {
    if (!date && board?.today) setDate(board.today);
  }, [board?.today, date]);

  const summary = board?.summary;

  const rows = useMemo(() => {
    const all = board?.rows || [];
    const q = search.trim().toLowerCase();

    const searched = q
      ? all.filter(
          (r) => r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q)
        )
      : all;

    switch (filter) {
      case 'no_plan':
        return searched.filter((r) => r.day_status === 'no_plan');
      case 'forgot_eod':
        return searched.filter((r) => r.day_status === 'forgot_eod');
      case 'late':
        return searched.filter((r) => r.is_late);
      case 'open_tasks':
        return searched.filter((r) => r.still_open > 0);
      default:
        return searched;
    }
  }, [board?.rows, search, filter]);

  const chip = (key: ExceptionFilter, label: string, count: number, color: any) => (
    <Chip
      label={`${label}: ${count}`}
      color={filter === key ? color : 'default'}
      variant={filter === key ? 'filled' : 'outlined'}
      onClick={() => setFilter(filter === key ? 'all' : key)}
      sx={{ fontWeight: 600, cursor: 'pointer' }}
    />
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Daily Plan Board
          </Typography>
          <Typography variant="body2" color="text.secondary">
            What everyone planned, what they finished, and what slipped.
            {board?.scope === 'team' && ' Showing your direct reports.'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <TextField
            type="date"
            label="Date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: board?.today }}
            sx={{
              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
              }
            }}
          />
          <TextField
            label="Search employee"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />
        </Box>

        {board && !board.is_company_working_day && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            This isn't a company working day, so nobody was expected to submit a plan.
            Anyone shown below chose to work.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3
          }}
        >
          <KpiCard
            label="Planned"
            value={`${summary?.planned ?? 0}/${summary?.expected_to_work ?? 0}`}
            sub="of those expected to work"
            gradient={KPI_GRADIENTS[0]}
          />
          <KpiCard
            label="Tasks completed"
            value={`${summary?.completed ?? 0}/${summary?.resolved ?? 0}`}
            sub="of tasks with a verdict"
            gradient={KPI_GRADIENTS[1]}
          />
          <KpiCard
            label="Completion rate"
            value={formatRate(summary?.completion_rate)}
            sub="completed vs. not completed"
            gradient={KPI_GRADIENTS[2]}
          />
          <KpiCard
            label="Day closed"
            value={summary?.eod_done ?? 0}
            sub={`${summary?.in_progress ?? 0} still working`}
            gradient={KPI_GRADIENTS[3]}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {chip('no_plan', "Didn't plan", summary?.not_planned ?? 0, 'error')}
          {chip('forgot_eod', 'No EOD', summary?.forgot_eod ?? 0, 'warning')}
          {chip('late', 'Late', summary?.late ?? 0, 'warning')}
          {chip(
            'open_tasks',
            'Open tasks',
            (board?.rows || []).filter((r) => r.still_open > 0).length,
            'info'
          )}
          <Chip label={`On leave: ${summary?.on_leave ?? 0}`} variant="outlined" sx={{ fontWeight: 600 }} />
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            {loading && !board ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">In</TableCell>
                      <TableCell align="center">Out</TableCell>
                      <TableCell align="center">Hours</TableCell>
                      <TableCell align="center">Tasks</TableCell>
                      <TableCell align="center">Done</TableCell>
                      <TableCell align="center">Slipped</TableCell>
                      <TableCell align="center">Completion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.employee_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelected(row)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.dept_name || row.role_name}
                              </Typography>
                            </Box>
                            {row.has_left && (
                              <Chip label="Left" size="small" variant="outlined" sx={{ height: 20 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <DayStatusChip status={row.day_status as DayStatus} />
                            {row.is_late && (
                              <Chip label="Late" size="small" color="warning" variant="outlined" sx={{ height: 20 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{timeOf(row.punch_in)}</TableCell>
                        <TableCell align="center">{timeOf(row.punch_out)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            {row.total_hours ?? '—'}
                            {row.is_retro && (
                              <Tooltip title="Filled in by a retroactive end-of-day report — a standard workday, not measured time.">
                                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{row.tasks_total || '—'}</TableCell>
                        <TableCell align="center">{row.completed || '—'}</TableCell>
                        <TableCell align="center">
                          {/* not_completed + carried + dropped — all three mean
                              "did not get done on this day". */}
                          {row.not_completed + row.carried + row.dropped || '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          {formatRate(row.completion_rate)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loading && rows.length === 0 && (
              <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                <Typography>
                  {board?.scope === 'team'
                    ? "You don't have any direct reports yet."
                    : 'No employees match this view.'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <EmployeeDayDialog
          open={!!selected}
          employeeId={selected?.employee_id || null}
          date={date || board?.today || ''}
          scope={board?.scope}
          onClose={() => setSelected(null)}
        />
      </Container>
    </Layout>
  );
};

export default PlanBoardPage;
